
import Vue from "vue";

// T2-6225: Central type → AP destination map. The AP has no dedicated
// :id detail routes, so links open the existing master-detail view / table
// (same tab) and pass the id as a query param so the destination view can
// select the row. Types with no view fall back to plain text.
type EntityType =
  | "location"
  | "job"
  | "shift"
  | "hygienist"
  | "talent"
  | "user"
  | "invoice"
  | "hireJob"
  | "partialOffer";

const ROUTE_MAP: Record<string, (id: number | string) => any | null> = {
  location: id => ({
    path: "/dental-offices",
    query: { locationId: String(id) }
  }),
  job: id => ({ path: "/tables/Job", query: { jobId: String(id) } }),
  shift: id => ({ path: "/tables/Shift", query: { jobId: String(id) } }),
  hygienist: id => ({ path: "/talent", query: { hygienistId: String(id) } }),
  talent: id => ({ path: "/talent", query: { hygienistId: String(id) } }),
  user: () => ({ path: "/tables/User" }),
  invoice: id => ({ path: "/invoices", query: { invoiceId: String(id) } }),
  // No detail view exists in the AP for these — render as plain text.
  hireJob: () => null,
  partialOffer: () => null
};

export default Vue.extend({
  name: "EntityIdLink",
  props: {
    type: { type: String as () => EntityType, required: true },
    id: { type: [Number, String], default: null },
    // Optional label override (defaults to "#<id>").
    label: { type: String, default: "" }
  },
  computed: {
    display(): string {
      if (this.id === null || this.id === undefined || this.id === "") {
        return "—";
      }
      return this.label || `#${this.id}`;
    },
    route(): any | null {
      if (this.id === null || this.id === undefined || this.id === "") {
        return null;
      }
      const builder = ROUTE_MAP[this.type];
      return builder ? builder(this.id) : null;
    },
    // Real href so the browser natively handles Cmd/Ctrl-click, middle-click
    // and "Open in new tab" from the context menu (T2-6225 QA round 2).
    href(): string {
      return this.route ? this.$router.resolve(this.route).href : "#";
    },
    ariaLabel(): string {
      return `Open ${this.type} ${this.id}`;
    }
  },
  methods: {
    onClick(event: MouseEvent) {
      // Modifier / non-primary clicks: let the browser open the href in a
      // new tab natively. Only stop the click from also selecting the row.
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.button !== 0
      ) {
        event.stopPropagation();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (!this.route) return;
      this.$router.push(this.route).catch(() => {
        /* ignore redundant navigation */
      });
    }
  }
});
